import * as React from 'react';

import { enumBrokeredOrderStatus } from '@rematter/frontend-lib-api-client';
import { ContentCard, ContentSection, TextField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import isEqual from 'lodash/isEqual';

import {
  $brokeredOrderForm,
  BrokeredOrderFormState,
} from '../../_state/brokeredOrderForm.state';
import { useTranslations } from '../../i18n/useTranslations';
import { BrokeredOrderDetailRecord } from '../../types/brokeredOrder';

import { BrokeredOrderFormFooterActions } from './BrokeredOrderFormFooterActions';

type Props = {
  order?: BrokeredOrderDetailRecord | null;
  onDiscard?: () => void;
  onSaveUpdates?: (data?: BrokeredOrderFormState) => void;
  isSaving?: boolean;
};

const formSelector = (state: BrokeredOrderFormState) => ({
  notes: state.notes,
});

export const BrokeredOrderTabOverview: React.FC<Props> = ({
  order,
  onDiscard,
  onSaveUpdates,
  isSaving = false,
}) => {
  const t = useTranslations();

  const canEdit =
    order?.status === enumBrokeredOrderStatus.Draft ||
    order?.status === enumBrokeredOrderStatus.Open;

  const selectedFormAtom = React.useMemo(
    () => selectAtom($brokeredOrderForm, formSelector, isEqual),
    [],
  );

  const form = useAtomValue(selectedFormAtom);
  const updateForm = useSetAtom($brokeredOrderForm);

  const handleNotesChange = React.useCallback(
    (value: string) => {
      updateForm((prev) => ({
        ...prev,
        notes: value,
      }));
    },
    [updateForm],
  );

  const supplierNames = order?.suppliers.map((s) => s.name).join(', ') || '—';
  const customerNames = order?.customers.map((c) => c.name).join(', ') || '—';

  return (
    <div className="@BrokeredOrderTabOverview flex max-h-full flex-col gap-5 p-5">
      <ContentCard className="p-0 pt-5">
        <ContentSection
          title={t.orders.edit.sections.orderInfo}
          variant="panel"
        >
          <div className="grid gap-4 md:grid-cols-2">
            <TextField
              label={t.orders.edit.fields.orderNumber}
              value={order?.orderNumber ?? ''}
              isReadOnly
            />
          </div>
        </ContentSection>

        <ContentSection title={t.orders.edit.sections.parties} variant="panel">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral/70">
                {t.orders.edit.fields.supplier}
              </label>
              <p className="text-sm">{supplierNames}</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-neutral/70">
                {t.orders.edit.fields.customer}
              </label>
              <p className="text-sm">{customerNames}</p>
            </div>
          </div>
        </ContentSection>

        <ContentSection
          title={t.common.sections.additionalInfo}
          variant="panel"
          hideDivider
        >
          <TextField
            label={t.common.labels.notes}
            value={form.notes}
            onChange={handleNotesChange}
            placeholder={t.common.placeholders.optionalNotes}
            textArea
            isDisabled={!canEdit}
          />
        </ContentSection>

        {canEdit && (
          <BrokeredOrderFormFooterActions
            onSaveUpdates={onSaveUpdates}
            onDiscard={onDiscard}
            isSaving={isSaving}
          />
        )}
      </ContentCard>
    </div>
  );
};
